import type Owner from '@ember/owner';
import { setOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import { task, waitForProperty } from 'ember-concurrency';
import type {
  BudgetForecastCapacity,
  BudgetForecastDataItem,
  BudgetForecastSegment,
  BudgetForecastVersion,
  Event,
  Holiday,
} from 'lumen/models';
import type { CapacityMeta } from 'lumen/models/budget-forecast/capacity';
import type { Meta } from 'lumen/models/budget-forecast/data-item';
import type BudgetForecastEntry from 'lumen/models/budget-forecast/entry';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  DATE_GRANULARITY,
  type DateGranularity,
} from 'lumen/utils/constants/budget-forecast-constants';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

interface RequestBudgetForecastParams {
  endDate: string;
  granularity: DateGranularity;
  startDate: string;
}

export interface CapacityWithMeta {
  capacities: BudgetForecastCapacity[];
  meta: CapacityMeta;
}

interface ErrorWithStatusCode {
  status: number;
}
export function isErrorWithStatusCode(
  error: unknown,
): error is ErrorWithStatusCode {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof error.status === 'number'
  );
}

export class BudgetForecastDataFetching {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];

  fetchEntriesTask = task(async (dataType: 'budget' | 'forecast') => {
    const TYPE_KEYS = {
      budget: 'budgetforecast/budget/entries',
      forecast: 'budgetforecast/forecast/entries',
    } as const;

    try {
      return await this.store.query<BudgetForecastEntry>(
        'budget-forecast/entry',
        {
          namespace: APIGATEWAY,
          typeKey: TYPE_KEYS[dataType],
        },
      );
    } catch (error) {
      await this.alerting.showErrorAlert({
        text: getAPIErrorMessage(error, this.intl),
      });
      this.errorTracking.captureException(error);

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  fetchEntryByIdTask = task({ restartable: true }, async (entryId: string) => {
    try {
      const entry = await this.store.findRecord<BudgetForecastEntry>(
        'budget-forecast/entry',
        entryId,
      );

      return entry;
    } catch (error) {
      this.errorTracking.captureException(error);

      // Caller is responsible for handling 404 errors.
      if (isErrorWithStatusCode(error) && error.status === 404) {
        throw error;
      }

      await this.alerting.showErrorAlert({
        text: getAPIErrorMessage(error, this.intl),
      });

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  requestCapacitiesTask = task(
    { restartable: true },
    async (params: RequestBudgetForecastParams): Promise<CapacityWithMeta> => {
      try {
        const capacities = await this.store.query<BudgetForecastCapacity>(
          'budget-forecast/capacity',
          {
            namespace: APIGATEWAY,
            subscription_id: this.hotelAndMarketService.hotel.id,
            date_aggregation: params.granularity,
            start_date: params.startDate,
            end_date: params.endDate,
          },
        );

        return {
          capacities,
          meta: capacities.meta as CapacityMeta,
        };
      } catch (error) {
        await this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  requestDataItemsTask = task(
    { restartable: true },
    async (
      params: RequestBudgetForecastParams & {
        budgetEntryId?: string;
        forecastEntryId?: string;
        versionId?: string;
      },
    ) => {
      try {
        const queryParams: Record<string, unknown> = {
          subscription_id: this.hotelAndMarketService.hotel.id,
          date_aggregation: params.granularity,
          start_date: params.startDate,
          end_date: params.endDate,
        };

        if (params.budgetEntryId) {
          queryParams['budget_entry_id'] = params.budgetEntryId;
        }

        if (params.forecastEntryId) {
          queryParams['forecast_entry_id'] = params.forecastEntryId;
        }

        if (params.versionId) {
          queryParams['version_id'] = params.versionId;
        }

        const response = await this.store.query<BudgetForecastDataItem>(
          'budget-forecast/data-item',
          {
            namespace: APIGATEWAY,
            typeKey: 'budgetforecast/budget-forecast',
            ...queryParams,
          },
        );

        await waitForProperty(this.requestSegmentsTask, 'isRunning', false);

        return {
          dataItems: [...response],
          meta: response.meta as Meta,
        };
      } catch (error) {
        await this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  requestEventsAndHolidaysTask = task(
    { restartable: true },
    async (params: RequestBudgetForecastParams) => {
      if (
        params.granularity !== DATE_GRANULARITY.DAY ||
        !this.access.hasFeature('app.events')
      ) {
        return { events: [], holidays: [] };
      }

      try {
        const requests = [
          this.store.query<Event>('event', {
            namespace: APIGATEWAY,
            typeKey: 'events/accepted',
            subscription_id: this.hotelAndMarketService.hotel.id,
            from_date_range_start: params.startDate,
            from_date_range_end: params.endDate,
          }),
          this.store.query<Holiday>('holiday', {
            namespace: APIGATEWAY,
            typeKey: 'holidays',
            subscription_id: this.hotelAndMarketService.hotel.id,
            from_date_range_start: params.startDate,
            from_date_range_end: params.endDate,
          }),
        ] as const;

        const [events, holidays] = await Promise.all(requests);

        return { events, holidays };
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  requestSegmentsTask = task(
    { restartable: true },
    async (params: { entryId?: string }) => {
      try {
        const queryParams: {
          entry_id?: string;
          subscription_id: string;
        } = {
          subscription_id: this.hotelAndMarketService.hotel.id!,
        };

        if (params.entryId) {
          queryParams.entry_id = params.entryId;
        }

        const segments = await this.store.query<BudgetForecastSegment>(
          'budget-forecast/segment',
          {
            namespace: APIGATEWAY,
            typeKey: 'budgetforecast/hierarchy',
            ...queryParams,
          },
        );

        return [...segments];
      } catch (error) {
        await this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  requestVersionsForEntryTask = task(
    { restartable: true },
    async (entryId: string) => {
      const queryParams = {
        subscription_id: this.hotelAndMarketService.hotel.id!,
      };

      try {
        return await this.store.query<BudgetForecastVersion>(
          'budget-forecast/version',
          {
            namespace: APIGATEWAY,
            typeKey: `budgetforecast/entries/${entryId}/versions`,
            ...queryParams,
          },
        );
      } catch (error) {
        await this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  constructor(owner: Owner) {
    setOwner(this, owner);
  }

  cancelAll(): void {
    void this.requestDataItemsTask.cancelAll();
    void this.requestSegmentsTask.cancelAll();
    void this.requestCapacitiesTask.cancelAll();
    void this.requestEventsAndHolidaysTask.cancelAll();
    void this.fetchEntriesTask.cancelAll();
  }

  unloadAll(): void {
    /**
     * In application tests, the app gets torn down after each test. This causes the store to be destroyed
     * before the deactivate hook is called. This check is to prevent the store from being accessed after it's destroyed.
     */
    if (this.store.isDestroyed) {
      return;
    }

    this.store.unloadAll('budget-forecast/data-item');
    this.store.unloadAll('budget-forecast/segment');
    this.store.unloadAll('budget-forecast/capacity');
  }
}
